import type {
    CompRef,
    DependenciesBuilder,
    Namespace,
    Dependencies,
    ReferencesMap,
    ReferencesManager,
    DependenciesManager,
    ReferencesBuilder,
    Pointer,
    ReferencedItem,
    ApplicationsBuilder,
    ApplicationsMap,
    ApplicationsManager
} from '@wix/document-services-types'
import {
    ExtendedRefInfo,
    namespaceToReferencedMap,
    referencedMapToNamespace,
    STRUCTURE_TYPE
} from '@wix/import-export-utils'
import {stripHashIfExists} from '../../../../../utils/refArrayUtils'
import {generateItemIdByNamespace, withHash} from '../../utils'
import _ from 'lodash'
import {CreateExtArgs, pointerUtils} from '@wix/document-manager-core'
import type {DataExtensionAPI} from '../../../../data'
import {ApplicationsManagerImpl} from './ApplicationsManager'
import {getInfoForItem} from './dependencyInfo'

const getReferencedMap = (namespace: Namespace): string => namespaceToReferencedMap[namespace] ?? namespace
const getReferencePointer = (id: string, referencedMap: string): Pointer =>
    pointerUtils.getPointer(id, referencedMapToNamespace[referencedMap] ?? referencedMap)

const buildDependencies = (references: ReferencesMap, applications: ApplicationsMap): Dependencies | undefined => {
    const dependencies: Dependencies = {references, applications}

    const cleanedDependencies: Partial<Dependencies> = _.omitBy<Dependencies>(
        dependencies,
        val => _.isUndefined(val) || _.keys(val).length === 0
    )
    if (Object.keys(cleanedDependencies).length === 0) {
        return undefined
    }

    return cleanedDependencies
}

const getReferencesManager = (original: ReferencesMap = {}): ReferencesManager => {
    const references: ReferencesMap = original

    const addReference = (id: string, namespaceOrReferencedMap: string, data?: Partial<ReferencedItem>): void => {
        const referencedMap = getReferencedMap(namespaceOrReferencedMap)
        const referenceItem: ReferencedItem = Object.assign({}, {target: id}, _.omitBy(data, _.isUndefined))

        references[referencedMap] ??= {}
        references[referencedMap][id] = referenceItem
    }

    const getTarget = (id: string, referencedMap: string): string | undefined => references[referencedMap]?.[id]?.target

    const setTarget = (id: string, referencedMap: string, target: string): void => {
        references[referencedMap] ??= {}
        references[referencedMap][id] = {target}
    }

    const getNewItemId = (itemId: string | undefined, namespace: string, originalItemId?: string): string => {
        if (!itemId) {
            return originalItemId ?? generateItemIdByNamespace(namespace)
        }

        let id = getTarget(itemId, namespace)
        if (!id) {
            id = originalItemId ?? generateItemIdByNamespace(namespace)
            addReference(itemId, namespace, {target: id})
        }

        return id
    }

    const getNewItemRef = (originalId: string | undefined, refFieldInfo: ExtendedRefInfo): string => {
        const {referencedMap, noHash, constantValues} = refFieldInfo

        if (originalId) {
            originalId = stripHashIfExists(originalId)

            if (constantValues?.has(originalId)) {
                return originalId
            }

            const id = getNewItemId(originalId, referencedMap)
            if (!noHash) {
                return withHash(id)
            }

            return id
        }

        const id = getNewItemId(originalId, referencedMap)
        if (!noHash) {
            return withHash(id)
        }

        return id
    }

    return {
        addReference,
        getTarget,
        setTarget,
        getNewItemId,
        getNewItemRef,
        getReferences: (): ReferencesMap => references
    }
}

const getApplicationsManager = (original: ApplicationsMap = {}): ApplicationsManager => {
    return new ApplicationsManagerImpl(original)
}

export const getDependenciesManager = (original: Dependencies = {}): DependenciesManager => {
    const references: ReferencesManager = getReferencesManager(original?.references)
    const applications: ApplicationsManager = getApplicationsManager(original?.applications)

    return {
        references,
        applications,
        getDependencies: (): Dependencies | undefined =>
            buildDependencies(references.getReferences(), applications.getApplications())
    }
}

type RefsMap = Record<Namespace, Set<string>>

const getReferencesBuilder = ({extensionAPI, dal}: CreateExtArgs, components: CompRef[]): ReferencesBuilder => {
    const nonOwnedRefs: RefsMap = {}
    const internalRefs: RefsMap = {
        [STRUCTURE_TYPE]: new Set(components.map(comp => comp.id))
    }

    const hasInternalRef = (ref: string, namespace: Namespace): boolean => internalRefs[namespace]?.has(ref) ?? false

    const addRef = (refMap: RefsMap, ref: string, namespace: Namespace): void => {
        refMap[namespace] ??= new Set()
        refMap[namespace].add(ref)
    }

    const buildReferenceMap = (): ReferencesMap => {
        const {data} = extensionAPI as DataExtensionAPI
        const referencesManager = getReferencesManager()

        for (const [namespace, refs] of Object.entries(nonOwnedRefs)) {
            for (const ref of refs) {
                if (!hasInternalRef(ref, namespace)) {
                    const pointer = getReferencePointer(ref, namespace)
                    const type = data.getItemType(pointer)
                    const item = dal.get(pointer)
                    const info = getInfoForItem(item)
                    referencesManager.addReference(ref, namespace, {type, info})
                }
            }
        }

        return referencesManager.getReferences()
    }

    return {
        addInternalRef: (ref: string, namespace: Namespace): void => addRef(internalRefs, ref, namespace),
        addNonOwnedRef: (ref: string, namespace: Namespace): void => addRef(nonOwnedRefs, ref, namespace),
        hasInternalRef,
        build: buildReferenceMap
    }
}

const getApplicationsBuilder = (): ApplicationsBuilder => {
    const applicationsManager = getApplicationsManager()
    return {
        addApplication: applicationsManager.addApplication.bind(applicationsManager),
        build: (): ApplicationsMap => applicationsManager.getApplications()
    }
}

export const getDependenciesBuilder = (
    extensionContext: CreateExtArgs,
    components: CompRef[] = []
): DependenciesBuilder => {
    const references: ReferencesBuilder = getReferencesBuilder(extensionContext, components)
    const applications: ApplicationsBuilder = getApplicationsBuilder()

    return {
        references,
        applications,
        build: (): Dependencies | undefined => buildDependencies(references.build(), applications.build())
    }
}

export const SPECIAL_DATA_NODE_IDS: Record<Namespace, Set<string>> = {
    data: new Set(['APPS_INSTALL_STATE', 'CUSTOM_MAIN_MENU', 'CUSTOM_MENUS', 'MAIN_MENU', 'PAGES_GROUP_COLLECTION']),
    props: new Set(['SITE_PAGES']),
    style: new Set(['THEME_DATA'])
}

export const SPECIAL_DATA_NODE_REFERENCE_MAP: ReferencesMap = _.reduce(
    SPECIAL_DATA_NODE_IDS,
    (references: ReferencesManager, idsSet: Set<string>, namespace: Namespace) => {
        idsSet.forEach((id: string) => references.addReference(id, namespace))
        return references
    },
    getReferencesManager()
).getReferences()
